import * as React from "react";
import { useState, useEffect } from "react";
import { useStyles } from "../../assets/styles/Theme";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Divider,
  IconButton,
  Collapse,
  FormHelperText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import dayjs from "dayjs";
import {
  CON_BUSINESS_TYPECODE,
  CON_STATUS_ACTIVE,
} from "../../utils/sys/Constants";
import { GRN_FORM_LABELS, GRN_SECTIONS, OPTIONS } from "../../utils/sys/GrnLabels";
import { GRN_DATE_FORMAT, FLAG } from "../../utils/sys/GrnConstants";
import { externalgetApi } from "../../services/externalApis";
import { SETTING_API_15 } from "../../api/ApiModel";
import { useSelector } from "react-redux";
import { AnyForNow } from "../../features/grn/types";

interface GrnDetailsProps {
  warehouse: string;
  setWarehouse: (warehouse: string) => void;
  partialShipments: string;
  setPartialShipments: (value: string) => void;
  deliveryDate: AnyForNow;
  setDeliveryDate: (date: AnyForNow) => void;
  poNumber: string;
  setPoNumber: (number: string) => void;
  poDate: AnyForNow;
  setPoDate: (date: AnyForNow) => void;
  invoiceNumber: string;
  setInvoiceNumber: (number: string) => void;
  invoiceDate: AnyForNow;
  setInvoiceDate: (date: AnyForNow) => void;
  dispatchNumber: string;
  setDispatchNumber: (number: string) => void;
  dispatchDate: AnyForNow;
  setDispatchDate: (date: AnyForNow) => void;
  awbNumber: string;
  setAwbNumber: (number: string) => void;
  billOfLandingNumber: string;
  setBillOfLandingNumber: (number: string) => void;
  deliveryNoteNumber: string;
  setDeliveryNoteNumber: (number: string) => void;
  errors: AnyForNow;
  setErrors: (errors: AnyForNow) => void;
  validateFields?: () => void;
  grnHeader?: AnyForNow;
  isEditMode?: boolean;
}

const GrnDetailsComponent: React.FC<GrnDetailsProps> = (props) => {
  const {
    warehouse,
    setWarehouse,
    partialShipments,
    setPartialShipments,
    deliveryDate,
    setDeliveryDate,
    poNumber,
    setPoNumber,
    poDate,
    setPoDate,
    invoiceNumber,
    setInvoiceNumber,
    invoiceDate,
    setInvoiceDate,
    dispatchNumber,
    setDispatchNumber,
    dispatchDate,
    setDispatchDate,
    awbNumber,
    setAwbNumber,
    billOfLandingNumber,
    setBillOfLandingNumber,
    deliveryNoteNumber,
    setDeliveryNoteNumber,
    errors,
    setErrors,
    validateFields,
    grnHeader,
    isEditMode,
  } = props;
  // Get user parameters from Redux store
  const SYS_USER = useSelector((state: AnyForNow) => state.user) || {};
  const { COMPCODE } = SYS_USER.USER_PARAM || {};
  const theme = useTheme();
  const styles = useStyles(theme);
  const [grnLocations, setGrnLocations] = useState<AnyForNow[]>([]);
  const [showOptional, setShowOptional] = useState<boolean>(false);
  const [showGrn, setshowGrn] = useState<boolean>(true);

  // Track initialization to prevent infinite loop - use both ID tracking and initialization flag
  const lastGrnHeaderIdRef = React.useRef<string | null>(null);
  const hasInitializedRef = React.useRef<boolean>(false);

  const toggleOptionalFields = () => {
    setShowOptional((prev) => !prev);
  };

  const toggleShowGrn = () => {
    setshowGrn((prev) => !prev);
  };

  useEffect(() => {
    // Skip if already initialized for this component instance
    if (hasInitializedRef.current) {
      return;
    }

    // Skip if not in edit mode or no header data
    if (!isEditMode || !grnHeader) {
      return;
    }

    // Get a stable ID to track initialization
    const currentGrnId = String(grnHeader?.grncode || grnHeader?.restcode || "");

    // Skip if no valid ID
    if (!currentGrnId) {
      return;
    }

    // Skip if we've already processed this exact GRN
    if (currentGrnId === lastGrnHeaderIdRef.current) {
      return;
    }

    // Perform initialization
    setWarehouse(grnHeader?.restcode || "");
    setDeliveryDate(
      grnHeader?.deliverynotedate ? dayjs(grnHeader.deliverynotedate) : null
    );
    setPoNumber(grnHeader?.ponumber || "");
    setPoDate(grnHeader?.podate ? dayjs(grnHeader.podate) : null);
    setInvoiceNumber(grnHeader?.invoiceno || "");
    setInvoiceDate(
      grnHeader?.invoicedate ? dayjs(grnHeader.invoicedate) : null
    );
    setDispatchNumber(grnHeader?.dispatchno || "");
    setDispatchDate(
      grnHeader?.dispatchedate ? dayjs(grnHeader.dispatchedate) : null
    );
    setAwbNumber(grnHeader?.awbno || "");
    setBillOfLandingNumber(grnHeader?.blno || "");
    setDeliveryNoteNumber(grnHeader?.deliverynoteno || "");
    setPartialShipments(grnHeader?.ispartialship === FLAG.YES ? FLAG.YES : FLAG.NO);

    // Mark as initialized
    lastGrnHeaderIdRef.current = currentGrnId;
    hasInitializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, grnHeader?.grncode, grnHeader?.restcode]); // Only depend on specific identifiers
  const fetchLocation = React.useCallback(async () => {
    if (!COMPCODE) return; // Don't fetch if COMPCODE is not available

    try {
      const params = {
        COMPCODE,
        BUSINESSTYPECODE: CON_BUSINESS_TYPECODE,
        STS: CON_STATUS_ACTIVE,
      };
      const response = await externalgetApi(SETTING_API_15, "GET", params);
      setGrnLocations(response); // Save doc types to
    } catch (error) {
      console.error("Error fetching GRN document types", error);
    }
  }, [COMPCODE]);

  useEffect(() => {
    fetchLocation();
  }, [fetchLocation]);

  return (
    <>
      <Box sx={styles.grn_supplier_container} mt={2}>
        <Box sx={styles.box_space_between}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
            onClick={toggleShowGrn}
          >
            <IconButton
              sx={{
                transform: showGrn ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
                mr: 1,
              }}
            >
              <ExpandMoreOutlinedIcon />
            </IconButton>
            <Typography sx={styles.grn_sub_title_text}>{GRN_SECTIONS.TITLE}</Typography>
          </Box>
          <Button
            variant="text"
            size="small"
            onClick={toggleOptionalFields}
            sx={styles.btn_text}
          >
            {showOptional
              ? GRN_SECTIONS.HIDE_ADDITIONAL
              : GRN_SECTIONS.ADDITIONAL_DETAILS}
          </Button>
        </Box>
        <Divider />
        <Collapse in={showGrn} timeout="auto" unmountOnExit>
          <Grid container spacing={2} mt={2}>
            <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <FormControl
                variant="outlined"
                required
                fullWidth
                error={Boolean(errors.warehouse)}
                size="small"
                sx={styles.ipt_helper_text}
              >
                <InputLabel>{GRN_FORM_LABELS.DELIVERY_LOCATION}</InputLabel>
                <Select
                  value={warehouse}
                  // onChange={handleWarehouseChange}
                  onChange={(e) => {
                    setWarehouse(e.target.value);
                    if (errors.warehouse) {
                      setErrors((prev) => ({ ...prev, warehouse: undefined }));
                    }
                  }}
                  label={GRN_FORM_LABELS.WAREHOUSE_LOCATION}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        boxShadow: 2, // shadow
                      },
                    },
                  }}
                >
                  {grnLocations.map((location) => (
                    <MenuItem key={location.restcode} value={location.restcode}>
                      {location.restname}
                    </MenuItem>
                  ))}
                </Select>
                {/* {errors.warehouse && (
                  <FormHelperText>{errors.warehouse}</FormHelperText>
                )} */
                //bug 7473 fixed
                }

              </FormControl>
            </Grid>{" "}
            <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={GRN_FORM_LABELS.DELIVERY_DATE}
                    format={GRN_DATE_FORMAT.SETUP}
                  // value={deliveryDate}
                  // onChange={(newValue) => {
                  //   setDeliveryDate(newValue);
                  //   if (errors.deliveryDate) {
                  //     setErrors((prev) => ({
                  //       ...prev,
                  //       deliveryDate: undefined,
                  //     }));
                  //   }
                  // }}
                  value={deliveryDate ? dayjs(deliveryDate) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      setDeliveryDate(newValue.format(GRN_DATE_FORMAT.STANDARD));
                    } else {
                      setDeliveryDate(null);
                    }

                    if (errors.deliveryDate) {
                      setErrors((prev) => ({
                        ...prev,
                        deliveryDate: undefined,
                      }));
                    }
                  }}
                  sx={styles.ipt_helper_text}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      required: true,
                      error: Boolean(errors.deliveryDate),
                      helperText: errors.deliveryDate,
                      // InputProps: {
                      //   endAdornment: (
                      //     <InputAdornment position="end">
                      //       <CalendarMonthIcon />
                      //     </InputAdornment>
                      //   ),
                      // },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <TextField
                fullWidth
                id="po-reference-number"
                label={GRN_FORM_LABELS.PO_NUMBER}
                variant="outlined"
                size="small"
                // value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                value={poNumber}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={GRN_FORM_LABELS.PO_DATE}
                  // value={poDate}
                  // onChange={setPoDate}
                  format={GRN_DATE_FORMAT.SETUP}
                  value={poDate ? dayjs(poDate) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      setPoDate(newValue.format(GRN_DATE_FORMAT.STANDARD));
                    } else {
                      setPoDate(null);
                    }

                    if (errors.poDate) {
                      setErrors((prev) => ({
                        ...prev,
                        poDate: undefined,
                      }));
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      // InputProps: {
                      //   endAdornment: (
                      //     <InputAdornment position="end">
                      //       <CalendarMonthIcon />
                      //     </InputAdornment>
                      //   ),
                      // },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <TextField
                fullWidth
                id="invoice-number"
                label={GRN_FORM_LABELS.INVOICE_NUMBER}
                variant="outlined"
                size="small"
                required
                value={invoiceNumber}
                // onChange={(e) => setInvoiceNumber(e.target.value)}
                error={Boolean(errors.invoiceNumber)}
                // helperText={errors.invoiceNumber}  bug 7473 fixed
                sx={styles.ipt_helper_text}
                onChange={(e) => {
                  setInvoiceNumber(e.target.value);
                  if (errors.invoiceNumber)
                    setErrors((prev) => ({ ...prev, invoiceNumber: "" }));
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={GRN_FORM_LABELS.INVOICE_DATE}
                   format={GRN_DATE_FORMAT.SETUP}
                  // value={invoiceDate}
                  // onChange={(newValue) => {
                  //   setInvoiceDate(newValue);
                  //   if (errors.invoiceDate) {
                  //     setErrors((prev) => ({
                  //       ...prev,
                  //       invoiceDate: undefined,
                  //     }));
                  //   }
                  // }}
                  value={invoiceDate ? dayjs(invoiceDate) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      setInvoiceDate(newValue.format(GRN_DATE_FORMAT.STANDARD));
                    } else {
                      setInvoiceDate(null);
                    }

                    if (errors.invoiceDate) {
                      setErrors((prev) => ({
                        ...prev,
                        invoiceDate: undefined,
                      }));
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      required: true,
                      error: Boolean(errors.invoiceDate),
                      helperText: errors.invoiceDate,
             
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <FormControl
                variant="outlined"
                required
                fullWidth
                error={Boolean(errors.partialShipments)}
                size="small"
                sx={styles.ipt_helper_text}
              >
                <InputLabel>{GRN_FORM_LABELS.PARTIAL_SHIPMENTS}</InputLabel>
                <Select
                  value={partialShipments}
                  onChange={(e) => {
                    setPartialShipments(e.target.value);
                    if (errors.partialShipments) {
                      setErrors((prev) => ({
                        ...prev,
                        partialShipments: undefined,
                      }));
                    }
                  }}
                  label={GRN_FORM_LABELS.PARTIAL_SHIPMENTS}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        boxShadow: 2, // shadow
                      },
                    },
                  }}
                >
                  <MenuItem value={FLAG.YES}>{OPTIONS.ALLOW}</MenuItem>
                  <MenuItem value={FLAG.NO}>{OPTIONS.NOT_ALLOW}</MenuItem>
                </Select>
                {/* {errors.partialShipments && (
                  <FormHelperText>{errors.partialShipments}</FormHelperText>
                )} */
                //bug 7473 fixed
                }
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <TextField
                fullWidth
                id="delivery-note-number"
                label={GRN_FORM_LABELS.DELIVERY_NOTE_NUMBER}
                variant="outlined"
                size="small"
                value={deliveryNoteNumber}
                onChange={(e) => setDeliveryNoteNumber(e.target.value)}
              />
            </Grid>
          </Grid>

          <Collapse in={showOptional} timeout="auto" unmountOnExit>
            <Grid container spacing={2} mt={2}>
              <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <TextField
                  fullWidth
                  id="dispatch-number"
                  label={GRN_FORM_LABELS.DISPATCH_NUMBER}
                  variant="outlined"
                  size="small"
                  value={dispatchNumber}
                  onChange={(e) => setDispatchNumber(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={GRN_FORM_LABELS.DISPATCH_DATE}
                    // value={dispatchDate}
                    // onChange={setDispatchDate}
                    value={dispatchDate ? dayjs(dispatchDate) : null}
                    onChange={(newValue) => {
                      if (newValue) {
                        setDispatchDate(newValue.format(GRN_DATE_FORMAT.STANDARD));
                      } else {
                        setDispatchDate(null);
                      }

                      if (errors.dispatchDate) {
                        setErrors((prev) => ({
                          ...prev,
                          dispatchDate: undefined,
                        }));
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",

                        // InputProps: {
                        //   endAdornment: (
                        //     <InputAdornment position="end">
                        //       <CalendarMonthIcon />
                        //     </InputAdornment>
                        //   ),
                        // },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <TextField
                  fullWidth
                  id="dispatch-number"
                  label={GRN_FORM_LABELS.AWB_NUMBER}
                  variant="outlined"
                  size="small"
                  value={awbNumber}
                  onChange={(e) => setAwbNumber(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
                <TextField
                  fullWidth
                  id="dispatch-number"
                  label={GRN_FORM_LABELS.BILL_OF_LADING_NUMBER}
                  variant="outlined"
                  size="small"
                  value={billOfLandingNumber}
                  onChange={(e) => setBillOfLandingNumber(e.target.value)}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Collapse>
      </Box>
    </>
  );
};

// Wrap with React.memo with custom comparison to prevent unnecessary re-renders
// Only compare values that actually matter for rendering, ignore function references
const GrnDetails = React.memo(GrnDetailsComponent, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render), false if different (re-render)
  // Compare state values
  if (prevProps.warehouse !== nextProps.warehouse) return false;
  if (prevProps.partialShipments !== nextProps.partialShipments) return false;
  if (prevProps.poNumber !== nextProps.poNumber) return false;
  if (prevProps.invoiceNumber !== nextProps.invoiceNumber) return false;
  if (prevProps.dispatchNumber !== nextProps.dispatchNumber) return false;
  if (prevProps.awbNumber !== nextProps.awbNumber) return false;
  if (prevProps.billOfLandingNumber !== nextProps.billOfLandingNumber) return false;
  if (prevProps.deliveryNoteNumber !== nextProps.deliveryNoteNumber) return false;
  if (prevProps.isEditMode !== nextProps.isEditMode) return false;

  // Compare dates by value (convert to string for comparison)
  const prevDeliveryDate = prevProps.deliveryDate?.toString?.() || prevProps.deliveryDate;
  const nextDeliveryDate = nextProps.deliveryDate?.toString?.() || nextProps.deliveryDate;
  if (prevDeliveryDate !== nextDeliveryDate) return false;

  const prevPoDate = prevProps.poDate?.toString?.() || prevProps.poDate;
  const nextPoDate = nextProps.poDate?.toString?.() || nextProps.poDate;
  if (prevPoDate !== nextPoDate) return false;

  const prevInvoiceDate = prevProps.invoiceDate?.toString?.() || prevProps.invoiceDate;
  const nextInvoiceDate = nextProps.invoiceDate?.toString?.() || nextProps.invoiceDate;
  if (prevInvoiceDate !== nextInvoiceDate) return false;

  const prevDispatchDate = prevProps.dispatchDate?.toString?.() || prevProps.dispatchDate;
  const nextDispatchDate = nextProps.dispatchDate?.toString?.() || nextProps.dispatchDate;
  if (prevDispatchDate !== nextDispatchDate) return false;

  // Compare errors object by stringification (shallow comparison won't work for objects)
  if (JSON.stringify(prevProps.errors) !== JSON.stringify(nextProps.errors)) return false;

  // Compare grnHeader by specific fields that matter
  const prevGrnCode = prevProps.grnHeader?.grncode;
  const nextGrnCode = nextProps.grnHeader?.grncode;
  if (prevGrnCode !== nextGrnCode) return false;

  const prevRestCode = prevProps.grnHeader?.restcode;
  const nextRestCode = nextProps.grnHeader?.restcode;
  if (prevRestCode !== nextRestCode) return false;

  // Don't compare function references (setWarehouse, setErrors, validateFields, etc.)
  // They may have new references but shouldn't trigger re-render

  return true; // Props are equal, skip re-render
});

export default GrnDetails;
